/**
 * Task 2 completion topic persistence.
 *
 * Propagates the already-selected current-run topic ID into the completion
 * payload (`topicId`). Does not select, fetch, rank, or retry topics.
 */

export const TASK2_LOCAL_STORAGE_TOPIC_KEY = 'task2_topic_id';

export type Task2PersistenceTopicSources = {
  currentRunServerTopicId?: unknown;
  voiceAgentTopicId?: unknown;
  reactTopicId?: unknown;
  localStorageTopicId?: unknown;
};

export type Task2CompletionPersistPayload = {
  taskCompleted: true;
  sessionId: string;
  topicId: string | null;
};

export function normalizeTask2TopicId(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return String(Math.trunc(value));
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed || trimmed === 'null' || trimmed === 'undefined') return null;
    return trimmed;
  }
  return null;
}

export function resolveTask2PersistenceTopicId(
  sources: Task2PersistenceTopicSources,
): string | null {
  return (
    normalizeTask2TopicId(sources.currentRunServerTopicId) ||
    normalizeTask2TopicId(sources.voiceAgentTopicId) ||
    normalizeTask2TopicId(sources.reactTopicId) ||
    normalizeTask2TopicId(sources.localStorageTopicId)
  );
}

export function readTask2LocalStorageTopicId(
  storage: Pick<Storage, 'getItem'> | null = typeof localStorage === 'undefined'
    ? null
    : localStorage,
): string | null {
  if (!storage) return null;
  try {
    return normalizeTask2TopicId(storage.getItem(TASK2_LOCAL_STORAGE_TOPIC_KEY));
  } catch {
    return null;
  }
}

export function captureVoiceAgentInitTopicId(
  initJson: { topicId?: unknown } | null | undefined,
): string | null {
  return normalizeTask2TopicId(initJson?.topicId);
}

export function buildTask2CompletionPersistPayload(
  sessionId: string,
  topicId: string | null,
): Task2CompletionPersistPayload {
  return {
    taskCompleted: true,
    sessionId,
    topicId,
  };
}
